import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Ban, CheckCircle, Loader2, Mail, XCircle } from 'lucide-react';

type StatusBadgeWithTooltipStaffProps = {
  status: string;
  userStatus?: string | null;
};

type StatusMeta = {
  label: string;
  badgeClass: string;
  icon: React.ReactNode;
  description: string;
  next: string;
  badgeText?: string;
};

const resolveStatusKey = (status: string, userStatus?: string | null) => {
  if (status === 'pending_acceptance' || (status === 'active' && userStatus === 'invited')) {
    return 'pending_acceptance';
  }

  if (status === 'blocked') {
    return 'suspended';
  }

  return status;
};

const getStatusMeta = (
  status: string,
  userStatus?: string | null
): StatusMeta => {
  const resolvedStatus = resolveStatusKey(status, userStatus);

  const baseMap: Record<string, StatusMeta> = {
    active: {
      label: 'Active',
      badgeClass: 'bg-emerald-500 hover:bg-emerald-500 text-white border-0 text-xs cursor-help',
      icon: <CheckCircle className="h-3 w-3 mr-1" />,
      description: 'Practitioner can access the system.',
      next: 'No action required.',
    },
    pending_acceptance: {
      label: 'Pending Acceptance',
      badgeClass: 'bg-amber-500 hover:bg-amber-500 text-white border-0 text-xs cursor-help',
      icon: <Mail className="h-3 w-3 mr-1" />,
      description: 'Invitation sent and awaiting acceptance.',
      next: 'Becomes Active once accepted.',
    },
    invited: {
      label: 'Invited',
      badgeClass: 'bg-blue-500 hover:bg-blue-500 text-white border-0 text-xs cursor-help',
      icon: <Mail className="h-3 w-3 mr-1" />,
      description: 'Practitioner has been invited but not activated.',
      next: 'Becomes Active after acceptance.',
    },
    pending: {
      label: 'Pending',
      badgeClass: 'bg-amber-500 hover:bg-amber-500 text-white border-0 text-xs capitalize cursor-help',
      icon: <Loader2 className="h-3 w-3 mr-1 animate-spin" />,
      description: 'Account setup is in progress.',
      next: 'Automatically moves to Active when complete.',
    },
    rejected: {
      label: 'Rejected',
      badgeClass: 'bg-red-500 hover:bg-red-500 text-white border-0 text-xs cursor-help',
      icon: <XCircle className="h-3 w-3 mr-1" />,
      description: 'Access request was denied.',
      next: 'You may resend an invite or submit a new request.',
    },
    suspended: {
      label: 'Suspended',
      badgeClass: 'bg-orange-500 hover:bg-orange-500 text-white border-0 text-xs cursor-help',
      icon: <Ban className="h-3 w-3 mr-1" />,
      description: 'Access is temporarily disabled.',
      next: 'Reinstate to activate or archive to disable permanently.',
    },
    archived: {
      label: 'Archived',
      badgeClass: 'bg-slate-500 hover:bg-slate-500 text-white border-0 text-xs cursor-help',
      icon: <Ban className="h-3 w-3 mr-1" />,
      description: 'Account is inactive and blocked.',
      next: 'Restore to make the practitioner Active again.',
    },
  };

  if (baseMap[resolvedStatus]) {
    return baseMap[resolvedStatus];
  }

  const fallback = resolvedStatus
    ? resolvedStatus.charAt(0).toUpperCase() + resolvedStatus.slice(1)
    : 'Unknown';

  return {
    label: fallback,
    badgeClass: 'bg-slate-100 hover:bg-slate-100 text-slate-800 border-0 text-xs cursor-help',
    icon: null,
    description: `Current status is "${fallback}".`,
    next: 'No automated transition is configured.',
  };
};

const StatusBadgeWithTooltipStaff = ({
  status,
  userStatus,
}: StatusBadgeWithTooltipStaffProps) => {
  const statusMeta = getStatusMeta(status, userStatus);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Badge className={statusMeta.badgeClass}>
            {statusMeta.icon}
            {statusMeta.badgeText || statusMeta.label}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-sm border-slate-900 bg-slate-900 text-slate-900 whitespace-normal break-words"
      >
        <div className="text-xs text-white">
          <div className="font-semibold">{statusMeta.label}</div>
          <div className="mt-1 whitespace-normal break-words">{statusMeta.description}</div>
          <div className="mt-1 whitespace-normal break-words text-slate-200">
            Next: {statusMeta.next}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default StatusBadgeWithTooltipStaff;

